/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { Modal, Button, Avatar, message, Spin, Popover } from "antd";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import styled from "@emotion/styled";
import moment from "moment";
import { URL } from "../../../helper/Mainurl";
import { Check } from "@mui/icons-material";
const token = localStorage.getItem("authToken");
const { Group: AvatarGroup } = Avatar;
// ===== Mock JSON Data =====
const notifications = [
  {
    id: 1,
    avatar: "https://picsum.photos/200",
    title: "Nativity rescheduled due to teacher sickness",
    time: "Monday 11.30AM",
    timeAgo: "2 hours ago",
    actions: ["Decline", "Accept"],
    active: true,
  },
  {
    id: 2,
    avatar: "https://picsum.photos/200",
    title: "Buy party food - Due in 2 days - ",
    link: { text: "See your Queue", href: "#" },
    time: "Monday 09.30AM",
    timeAgo: "4 hours ago",
  },
  {
    id: 3,
    avatar: "https://picsum.photos/200",
    title: "@Emily update task (Research & Planning)",
    time: "Monday 08.00AM",
    timeAgo: "5 hours ago",
  },
  {
    id: 4,
    avatar: "https://picsum.photos/200",
    title: "@Thomas update task (Research & Planning)",
    time: "Monday 07.00AM",
    timeAgo: "6 hours ago",
  },
];

// ===== Styled Components =====
const StyledModalWrapper = styled.div`
  position: relative;
  padding: 24px;
  background: white;
  border-radius: 16px;
  max-width: 500px;
  margin: auto;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 12px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
  background-color: ${({ unread }) => (unread ? "#f7f1ff" : "transparent")};

  &:hover {
    background-color: ${({ unread }) =>
      unread ? "#f1e8ff" : "rgba(0, 0, 0, 0.03)"};
  }
`;

const Content = styled.div`
  margin-left: 12px;
  flex: 1;
`;

const Title = styled.div`
  font-size: 16px;
  color: #121212;
  line-height: 1.4;

  a {
    color: #121212;
    font-weight: 600;
    text-decoration: underline;
  }

  strong {
    font-weight: 400;
  }
`;

const Time = styled.div`
  font-size: 13px;
  color: #777;
  margin-top: 4px;
`;

const TimeAgo = styled.div`
  font-size: 13px;
  color: #999;
  margin-top: 4px;
  text-align: right;
  margin-left: auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;

  .ant-btn {
    border-radius: 8px;
    font-weight: 600;
    min-width: 80px;
  }

  .ant-btn-primary {
    background: #9b5de5;
    border: none;
  }

  .ant-btn-dangerous {
    background: #fce4ff;
    color: #9b5de5;
    border: none;
  }
`;

const OutsideCloseButton = styled.div`
  position: absolute;
  top: -48px;
  right: -22px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1000;
`;

const NotificationModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchNotification();
  }, []);

  const handleDecline = async (id, status) => {
    try {
      const response = await fetch(`${URL}/v1/notification/action/${id}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (response.ok && data.code === 200) {
        message.success(
          `Notification ${status === "accepted" ? "accepted" : "declined"}!`
        );
        // Optionally remove or update the notification in the list
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      } else {
        message.error(data.message || "Action failed.");
      }
    } catch (err) {
      message.error("Failed to update notification.");
    } finally {
    }
  };
  const fetchNotification = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${URL}/v1/notification/list`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      let data = await response.json();
      // console.log("data",data.data.notifications)
      setNotifications(data.data.notifications);
    } catch (err) {
      message.error("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      if (!token) throw new Error("No auth token found");

      const response = await fetch(
        `${URL}/v1/notification/${id}/mark-as-read`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to mark notification as read");

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === id ? { ...notif, is_read: true } : notif
        )
      );
      fetchNotification(); // Refresh the notifications list
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };
  // Show up to 3 avatars, then a "+N" avatar if more
  const renderAvatars = (users) => {
    const maxToShow = 1;
    const extraCount = users.length - maxToShow;
    const extraUsers = users.slice(maxToShow);

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        {users.slice(0, maxToShow).map((user, idx) => (
          <Avatar
            key={idx}
            src={user.avtar}
            size={40}
            style={{ border: "2px solid #fff" }}
            title={user.name}
          />
        ))}
        {extraCount > 0 && (
          <Popover
            placement="top"
            content={
              <div style={{ display: "flex", gap: 4 }}>
                {extraUsers.map((user, idx) => (
                  <Avatar
                    key={idx}
                    src={user.avtar}
                    size={40}
                    style={{ border: "2px solid #fff" }}
                    title={user.name}
                  />
                ))}
              </div>
            }
          >
            <Avatar
              style={{
                backgroundColor: "#9B5DE5",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                border: "2px solid #fff",
                cursor: "pointer",
                marginLeft: 3,
              }}
              size={40}
              title={`+${extraUsers.length} more`}
            >
              {`+${extraUsers.length}`}
            </Avatar>
          </Popover>
        )}
      </div>
    );
  };
  return (
    <Modal
      open={open}
      footer={null}
      closable={true}
      centered
      onCancel={onClose}
      width={500}
      style={{ padding: 0 }}
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <StyledModalWrapper>
            <OutsideCloseButton onClick={onClose}>
              <CloseIcon fontSize="medium" />
            </OutsideCloseButton>
            {notifications.length == 0 && (
              <h4>You're all caught up! No new notifications.</h4>
            )}
            {notifications.map((note) => (
              <NotificationItem key={note.id} unread={!note.is_read}>
                {renderAvatars([
                  ...(note.assignees || []),
                  ...(note.collaborators || []),
                ])}
                <Content>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Title>
                      {note.title}
                      {note.link && (
                        <>
                          <a href={note.link.href}> {note.link.text}</a>
                        </>
                      )}
                    </Title>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Time>
                      {moment().diff(moment.utc(note.created_at), "days") > 6
                        ? moment.utc(note.created_at).format("DD MMM YYYY")
                        : moment.utc(note.created_at).format("dddd hh:mm A")}
                    </Time>
                    <TimeAgo>{note.created_at_human}</TimeAgo>
                    <div className={`flex items-center space-x-2 ml-4`}>
                      {!note.is_read && (
                        <div className="w-2 h-2 bg-purple-500 rounded-full" />
                      )}
                      {!note.is_read && (
                        <button
                          onClick={() => markNotificationAsRead(note.id)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Mark as read"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {note?.category === "invitation-sent" && (
                    <ActionButtons>
                      <Button
                        style={{ fontWeight: "400" }}
                        danger
                        size="medium"
                        onClick={() => handleDecline(note.id, "dismissed")}
                      >
                        Decline
                      </Button>
                      <Button
                        style={{ fontWeight: "400" }}
                        type="primary"
                        size="medium"
                        onClick={() => handleDecline(note.id, "accepted")}
                      >
                        Accept
                      </Button>
                    </ActionButtons>
                  )}
                </Content>
              </NotificationItem>
            ))}
          </StyledModalWrapper>
        </>
      )}
    </Modal>
  );
};

export default NotificationModal;
